import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import back4app2 from '../../back4app2';
import { AuthenticationError, AuthorizationError, NetworkError } from '@back4app2/sdk';
import { BACK4APP_DOT_COM_SITE_URL } from '../../settings';
import { extractParseAppOptions, ParseAppOption } from '../../utils/parseApps';

// Ensure users always have enough time to see the "linking" screen,
// even when associate finishes very fast.
const MIN_VALUE_SCREEN_MS = 3000;


function sleep(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

function DatabaseIcon({ className }: { className?: string }) {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M21 5v14c0 1.66-4.03 3-9 3s-9-1.34-9-3V5" />
      <path d="M21 12c0 1.66-4.03 3-9 3s-9-1.34-9-3" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={className}>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

export type LinkAppPhase = 'idle' | 'loading' | 'linking' | 'success' | 'error';

interface LinkAppModalProps {
  open: boolean;
  agentId?: string;
  onClose: () => void;
  onDone: () => void;
  // Controlled phase (to survive AppPanel remounts)
  phase?: LinkAppPhase;
  onPhaseChange?: (phase: LinkAppPhase) => void;
}

/**
 * LinkAppModal
 *
 * Lets users pick an existing Parse App (dashboard UUID) and links it to the agent:
 * setAgentCurrentParseApp (backend will sync metadata)
 */
export default function LinkAppModal({ 
  open, 
  agentId, 
  onClose, 
  onDone,
  phase: externalPhase,
  onPhaseChange,
}: LinkAppModalProps) {
  // Use controlled phase if provided, otherwise use internal state
  const isPhaseControlled = typeof externalPhase === 'string' && typeof onPhaseChange === 'function';
  const [internalPhase, setInternalPhase] = useState<LinkAppPhase>('idle');
  
  const phase = isPhaseControlled ? externalPhase : internalPhase;
  const setPhase = useCallback((p: LinkAppPhase | ((prev: LinkAppPhase) => LinkAppPhase)) => {
    if (isPhaseControlled) {
      const newPhase = typeof p === 'function' ? p(externalPhase!) : p;
      onPhaseChange?.(newPhase);
    } else {
      setInternalPhase(p);
    }
  }, [isPhaseControlled, externalPhase, onPhaseChange]);

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [options, setOptions] = useState<ParseAppOption[]>([]);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');

  
  // Track if we've already loaded data for this modal session
  const hasLoadedRef = useRef(false);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return options;
    return options.filter((o) => o.appName.toLowerCase().includes(s) || o.dashboardId.toLowerCase().includes(s));
  }, [options, search]);

  // Reset hasLoadedRef when modal closes
  useEffect(() => {
    if (!open) {
      hasLoadedRef.current = false;
    }
  }, [open]);

  // Load apps when modal opens (only once per session)
  useEffect(() => {
    if (!open) return;
    
    // Don't load if we're in an active phase (linking/success)
    // This happens when AppPanel remounts during the linking operation
    if (phase === 'linking' || phase === 'success') {
      return;
    }
    
    // Don't load if we've already loaded in this session
    if (hasLoadedRef.current) {
      return;
    }
    
    hasLoadedRef.current = true;
    setPhase('loading');
    setErrorMessage(undefined);
    setSelectedId(undefined);
    setSearch('');

    (async () => {
      try {
        const raw = await back4app2.getParseApps();
        const parsed = extractParseAppOptions(raw);
        setOptions(parsed);
        setPhase('idle');
      } catch (e) {
        if (e instanceof NetworkError) {
          setErrorMessage('Network error while loading apps. Please try again.');
        } else if (e instanceof AuthenticationError || e instanceof AuthorizationError) {
          window.location.replace(
            `${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`
          );
          return;
        } else {
          console.error('Error loading parse apps', e);
          setErrorMessage('Unexpected error while loading apps.');
        }
        setPhase('error');
      }
    })();
  }, [open, phase, setPhase]);

  const runLink = useCallback(async () => {
    if (!agentId) {
      setErrorMessage('Missing agent identifier. Please go back and try again.');
      setPhase('error');
      return;
    }
    if (!selectedId) return;

    setPhase('linking');
    setErrorMessage(undefined);
    try {
      // Run minimum "value screen" delay in parallel with the real backend work.
      // Gate = max(MIN_VALUE_SCREEN_MS, associate time).
      const minDelay = sleep(MIN_VALUE_SCREEN_MS);
      const work = back4app2.setAgentCurrentParseApp(agentId, selectedId);
      await Promise.all([minDelay, work]);
      // Move to success phase instead of calling onDone immediately
      setPhase('success');
    } catch (e) {
      if (e instanceof NetworkError) {
        setErrorMessage('Network error while linking app. Please try again.');
      } else if (e instanceof AuthenticationError || e instanceof AuthorizationError) {
        window.location.replace(
          `${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`
        );
        return;
      } else {
        console.error('Error linking app', e);
        setErrorMessage((e as any)?.message || 'Unexpected error while linking app.');
      }
      setPhase('error');
    }
  }, [agentId, selectedId, setPhase]);

  const handleGetStarted = useCallback(() => {
    onDone();
  }, [onDone]);

  // Only allow closing in safe phases (not during linking or success)
  const closeIfAllowed = useCallback(() => {
    if (phase === 'linking' || phase === 'success') return;
    onClose();
  }, [onClose, phase]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-xl bg-[#1e293b]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.5)] overflow-hidden">
        <div className="relative p-6">
          {/* Close button - hide during linking and success */}
          {phase !== 'linking' && phase !== 'success' && (
            <button
              className="absolute right-4 top-4 text-white/40 hover:text-white/80 transition-colors"
              onClick={closeIfAllowed}
              type="button"
              title="Close"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18" />
                <path d="M6 6l12 12" />
              </svg>
            </button>
          )}

          {/* ─── PHASE: Success ─────────────────────────────────────────── */}
          {phase === 'success' ? (
            <>
              <div className="flex flex-col items-center">
                {/* Success checkmark icon with glow */}
                <div className="w-16 h-16 rounded-full bg-[#27AE60]/20 flex items-center justify-center shadow-[0_0_40px_rgba(39,174,96,0.4)]">
                  <CheckIcon className="text-[#27AE60]" />
                </div>

                <h2 className="mt-4 text-xl font-semibold text-white text-center font-sora tracking-tight">
                  Your backend is ready!
                </h2>

                <p className="mt-2 text-sm text-white/60 text-center max-w-lg leading-relaxed">
                  Your agent is now linked. Database, APIs, Auth, Functions, and Hosting are live. Preview will appear on the right panel.
                </p>
              </div>

              {/* Action button — with glow effect */}
              <div className="mt-8 flex items-center justify-center">
                <button
                  type="button"
                  onClick={handleGetStarted}
                  className="px-7 py-3 rounded-xl text-white font-semibold tracking-wide transition-all duration-200 min-w-[170px] shadow-[0_0_20px_rgba(39,174,96,0.4)] hover:shadow-[0_0_30px_rgba(39,174,96,0.6)] transform hover:scale-[1.02]"
                  style={{ backgroundColor: '#27AE60' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#19875F'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#27AE60'; }}
                >
                  Get started
                </button>
              </div>
            </>
          ) : phase === 'linking' ? (
            <>
              {/* ─── PHASE: Linking (Progress) ─────────────────────────────── */}
              <div className="flex flex-col items-center">
                {/* Database icon with pulse effect */}
                <div className="w-16 h-16 rounded-full bg-[#208AEC]/15 flex items-center justify-center shadow-[0_0_25px_rgba(32,138,236,0.3)] animate-pulse">
                  <DatabaseIcon className="text-[#208AEC]" />
                </div>

                <h2 className="mt-4 text-xl font-semibold text-white text-center font-sora tracking-tight">
                  Linking your backend…
                </h2>

                <p className="mt-2 text-sm text-white/60 text-center max-w-lg leading-relaxed">
                  Connecting your app to the agent. This may take a few seconds.
                </p>
              </div>

              {/* Spinner with glow */}
              <div className="mt-8 flex justify-center">
                <div className="w-12 h-12 rounded-full border-4 border-[#208AEC]/20 border-t-[#208AEC] animate-spin shadow-[0_0_15px_rgba(32,138,236,0.3)]" />
              </div>

              <div className="mt-6 text-xs text-white/40 text-center">
                Setting up Preview, Database, Cloud Code, Authentication, and Hosting…
              </div>
            </>
          ) : (
            <>
              {/* ─── PHASE: Selection (idle/loading/error) ──────────────── */}
              <div className="flex flex-col items-center">
                {/* Database/Backend icon */}
                <div className="w-16 h-16 rounded-full bg-[#208AEC]/10 flex items-center justify-center">
                  <DatabaseIcon className="text-[#208AEC]" />
                </div>
                <h2 className="mt-4 text-xl font-semibold text-white text-center font-sora">Link existing backend</h2>
                <p className="mt-2 text-sm text-white/60 text-center max-w-lg">
                  Select an existing backend to link with this agent. This may take ~10–30 seconds.
                </p>
              </div>

              <div className="mt-6">
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by app name…"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#208AEC]/50 transition-colors"
                  disabled={phase === 'loading'}
                />
              </div>

              <div className="mt-4 max-h-72 overflow-y-auto glass-scroll pr-1">
                {phase === 'loading' ? (
                  <div className="text-sm text-white/50 py-6 text-center">Loading apps…</div>
                ) : filtered.length === 0 ? (
                  <div className="text-sm text-white/50 py-6 text-center">
                    No apps found. You can create a new one instead.
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {filtered.map((o) => {
                      const selected = selectedId === o.dashboardId;
                      return (
                        <button
                          key={o.dashboardId}
                          type="button"
                          onClick={() => setSelectedId(o.dashboardId)}
                          className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${
                            selected ? 'bg-[#208AEC]/10 border-[#208AEC]/40' : 'bg-white/5 border-white/10 hover:bg-white/10'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-white/90 truncate">{o.appName}</div>
                              <div className="text-xs text-white/40 truncate">{o.dashboardId}</div>
                            </div>
                            <div
                              className={`w-4 h-4 rounded-full border ${
                                selected ? 'bg-[#208AEC] border-[#208AEC]' : 'border-white/30'
                              }`}
                            />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {phase === 'error' && errorMessage ? (
                <div className="mt-4 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl p-3">
                  {errorMessage}
                </div>
              ) : null}

              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 transition-colors"
                  type="button"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 rounded-xl bg-[#27AE60] hover:bg-[#19875F] text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={runLink}
                  disabled={!selectedId || phase === 'loading'}
                >
                  Link
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
